import { FunctionComponent, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import AppLogo from "@/assets/images/favicon.png";

import { ROUTE_CONFIG } from "@/routes/config";

import { skipsCheckout, useOnboardingVariant } from "@/utils/useOnboardingVariant";

import { InputField } from "@/components/InputField/InputField";
import { Button } from "@/components/Button/Button";
import { ButtonSize } from "@/components/Button/types";
import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { ERROR_TYPE, getAllErrors, renderErrorMessages } from "@/utils";
import { useIpLocationSync } from "@/utils/useIpLocationSync";

import { OnboardingSettings, OnboardingSettingsSchema } from "../schema/onboarding";

import { useSaveOnboardingSettingsMutation } from "../../../redux/account/account.api";
import { useLazyGetCompanyEnrichCompanyDetailQuery } from "../../../redux/companyEnrich/companyEnrich.api";
import { OnboardingStepper } from "../components/OnboardingStepper";
import {
	LinkedinProfileCombobox,
	LinkedinProfileSelection,
} from "../components/LinkedinProfileCombobox";
import {
	CompanyLinkedinCombobox,
	CompanyLinkedinSelection,
} from "../components/CompanyLinkedinCombobox";
import { companyPrefillSelection } from "../utils/companyPrefillSelection";

/** Turns a picked company's bare domain ("acme.com") into the website URL the API stores. */
const toWebsiteUrl = (domain?: string | null): string | undefined => {
	const cleaned = domain
		?.trim()
		.replace(/^https?:\/\//, "")
		.replace(/\/+$/, "");

	return cleaned ? `https://${cleaned}` : undefined;
};

/**
 * Profile & Company step of the pre-checkout onboarding flow (PREC-3653):
 * pick your personal LinkedIn profile (typeahead with manual-URL fallback) and
 * confirm the company data pre-filled from the signup email domain.
 */
export const OnboardingProfile: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`onboarding.profileStep.${key}`);

	const { user } = useAppSelector(authSelector);
	const navigate = useNavigate();

	const variant = useOnboardingVariant();

	useIpLocationSync(user);

	const [saveOnboardingSettings, { isLoading, error }] = useSaveOnboardingSettingsMutation();

	const [selectedProfile, setSelectedProfile] = useState<LinkedinProfileSelection | null>(null);
	const [manualUrlVisible, setManualUrlVisible] = useState(false);
	// A LinkedIn URL pre-filled from the account shows as a confirmed chip in the
	// combobox — the manual field is only ever the "can't find my company" fallback.
	const [selectedCompany, setSelectedCompany] = useState<CompanyLinkedinSelection | null>(() =>
		companyPrefillSelection(user?.company)
	);

	const initialState: OnboardingSettings = {
		companyWebsite: user?.company?.website ?? undefined,
		linkedinUrl: user?.company?.linkedinUrl ?? undefined,
		personalLinkedinUrl: undefined,
	};

	const [manualCompanyUrlVisible, setManualCompanyUrlVisible] = useState(false);

	const [currentFormState, setCurrentFormState] = useState<OnboardingSettings>(initialState);
	// Async callbacks (the company-LinkedIn suggestion) must read the latest
	// form state, not a stale render closure — the ref always mirrors it.
	const formStateRef = useRef<OnboardingSettings>(initialState);

	const {
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<OnboardingSettings>({
		defaultValues: initialState,
		resolver: zodResolver(OnboardingSettingsSchema),
	});

	const formErrors = Object.values(errors).map((err) => err.message) as ERROR_TYPE[];

	const updateFormState = (name: keyof OnboardingSettings, value: string | undefined) => {
		const newState = { ...formStateRef.current, [name]: value };

		formStateRef.current = newState;
		setCurrentFormState(newState);
		reset(newState);
	};

	const [fetchCompanyDetail] = useLazyGetCompanyEnrichCompanyDetailQuery();

	/** The website field still holds the signup prefill (the email domain), untouched by the user. */
	const websiteIsSignupPrefill = () =>
		!formStateRef.current.companyWebsite ||
		formStateRef.current.companyWebsite === initialState.companyWebsite;

	/**
	 * Applies a picked company. `adoptWebsite` moves its domain into the website
	 * field — the picked company *is* the company, so leaving the email-domain
	 * prefill in place would send the wrong domain to the ICP research.
	 */
	const applyCompanySelection = (selection: CompanyLinkedinSelection, adoptWebsite: boolean) => {
		const website = toWebsiteUrl(selection.domain);

		setSelectedCompany(selection);
		setManualCompanyUrlVisible(false);

		if (website && adoptWebsite) {
			updateFormState("companyWebsite", website);
		}

		updateFormState("linkedinUrl", selection.linkedinUrl);
	};

	/**
	 * Picking a profile also suggests the LinkedIn page of the person's current
	 * company — but never overwrites a value the user already entered.
	 */
	const suggestCompanyLinkedin = async (selection: LinkedinProfileSelection) => {
		if (!selection.companyEnrichCompanyId || formStateRef.current.linkedinUrl) {
			return;
		}

		try {
			const company = await fetchCompanyDetail(selection.companyEnrichCompanyId).unwrap();

			if (company.linkedinUrl && !formStateRef.current.linkedinUrl) {
				// A mere suggestion — it may only fill a website the user hasn't set.
				applyCompanySelection(
					{
						name: company.name ?? company.domain ?? "",
						domain: company.domain,
						logoUrl: company.logoUrl,
						linkedinUrl: company.linkedinUrl,
					},
					websiteIsSignupPrefill()
				);
			}
		} catch (err) {
			console.error(err);
		}
	};

	const handleCompanyClear = () => {
		setSelectedCompany(null);
		updateFormState("linkedinUrl", undefined);
	};

	const handleCompanyManualEntry = () => {
		setSelectedCompany(null);
		setManualCompanyUrlVisible(true);
		updateFormState("linkedinUrl", undefined);
	};

	const handleProfileSelect = (selection: LinkedinProfileSelection) => {
		setSelectedProfile(selection);
		setManualUrlVisible(false);
		updateFormState("personalLinkedinUrl", selection.linkedinUrl);
		suggestCompanyLinkedin(selection);
	};

	const handleProfileClear = () => {
		setSelectedProfile(null);
		updateFormState("personalLinkedinUrl", undefined);
	};

	const handleManualEntry = () => {
		setSelectedProfile(null);
		setManualUrlVisible(true);
		updateFormState("personalLinkedinUrl", undefined);
	};

	const onSubmit = async (values: OnboardingSettings) => {
		try {
			await saveOnboardingSettings(values).unwrap();

			navigate(ROUTE_CONFIG.ONBOARDING_CHAT);
		} catch (err) {
			console.error(err);
		}
	};

	const greeting = user?.firstName
		? t("onboarding.profileStep.titleWithName", { name: user.firstName })
		: ts("title");

	return (
		<div className="w-full flex flex-col items-center justify-center">
			<div className="flex flex-col bg-white px-6 py-10 rounded-[20px] w-[428px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center mb-6">
				<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
				<div className="text-center text-md font-bold mb-4">{greeting}</div>
				<div className="text-center text-ssm font-normal mb-8">{ts("subtitle")}</div>

				<div className="text-left text-xs font-normal w-full mb-4">
					<div className="label-styles mb-1">{ts("personalLinkedinLabel")}</div>
					<LinkedinProfileCombobox
						value={selectedProfile}
						onClear={handleProfileClear}
						onManualEntry={handleManualEntry}
						onSelect={handleProfileSelect}
					/>
				</div>

				{manualUrlVisible && (
					<div className="text-left text-xs font-normal w-full mb-4">
						<InputField
							error={!!errors.personalLinkedinUrl?.message}
							handleChange={(event) =>
								updateFormState("personalLinkedinUrl", event.target.value || undefined)
							}
							label={ts("personalLinkedinUrlLabel")}
							name="personalLinkedinUrl"
							placeholder="https://www.linkedin.com/in/yourprofile"
							showError={!!errors.personalLinkedinUrl?.message}
							value={currentFormState.personalLinkedinUrl || ""}
						/>
					</div>
				)}

				<div className="text-left text-xs font-normal w-full mb-4">
					<InputField
						handleChange={(event) => updateFormState("companyWebsite", event.target.value)}
						label={ts("companyWebsiteLabel")}
						name="companyWebsite"
						placeholder={ts("companyWebsitePlaceholder")}
						rightInformation={{
							label: ts("whyWeNeedThis"),
							information: <>{ts("companyWebsiteInfo")}</>,
						}}
						value={currentFormState.companyWebsite || ""}
					/>
				</div>

				<div className="text-left text-xs font-normal w-full mb-4">
					<div className="label-styles mb-1">{ts("companyLinkedinLabel")}</div>
					<CompanyLinkedinCombobox
						value={selectedCompany}
						onClear={handleCompanyClear}
						onManualEntry={handleCompanyManualEntry}
						onSelect={(selection) => applyCompanySelection(selection, true)}
					/>
				</div>

				{manualCompanyUrlVisible && (
					<div className="text-left text-xs font-normal w-full mb-4">
						<InputField
							handleChange={(event) => updateFormState("linkedinUrl", event.target.value)}
							label={ts("companyLinkedinUrlLabel")}
							name="linkedinUrl"
							placeholder={ts("companyLinkedinPlaceholder")}
							rightInformation={{
								label: ts("howToFind"),
								information: (
									<>
										{ts("companyLinkedinInfoBefore")}{" "}
										<a
											href="https://www.linkedin.com/feed/"
											rel="noreferrer noopener"
											target="_blank"
										>
											https://www.linkedin.com/feed/
										</a>{" "}
										{ts("companyLinkedinInfoAfter")}
									</>
								),
							}}
							value={currentFormState.linkedinUrl || ""}
						/>
					</div>
				)}

				<div className="w-full">
					<Button
						className="w-full"
						isLoading={isLoading}
						size={ButtonSize.ML}
						title={ts("continue")}
						onClick={handleSubmit(onSubmit)}
					/>
				</div>

				<div className="mt-4">
					{getAllErrors(error, formErrors).length
						? renderErrorMessages(getAllErrors(error, formErrors))
						: null}
				</div>
			</div>
			<OnboardingStepper step={1} total={skipsCheckout(variant) ? 3 : 4} />
		</div>
	);
};
